import { Dispatch } from "redux"
import Konva from "konva"

import { PENDING } from "const"
import {
    Lifecycle,
    LifecyclePhase,
    LifecycleConnection,
} from "server/models"
import {
    AddLifecycleConnection,
    setLifecycleModelLoadState,
    setLifecycleModelReload,
    setLifecycleModelDrawConnection,
    setLifecyclePhaseActive,
    setLifecycleConnectionActive,
} from "client/store"

import { top, bottom, left, right } from "../const"
import { Side } from "./phaseGui"
import { GetOrthogonalPoints } from "./connectionPoints"
import GetRelativePosition from "./getPosition"


export const StartDrawConnection = (
    e: Konva.KonvaEventObject<MouseEvent>,
    dataWrapper: Konva.Layer,
    lifecycle: Lifecycle,
    parentPhaseId: number,
    startPhase: LifecyclePhase,
    startPhaseSide: Side,
    dispatch: Dispatch,
) => {
    e.cancelBubble = true
    if (e.evt.button !== 0) return

    const start = GetRelativePosition(dataWrapper, e.currentTarget) // start GUI point
    const end = GetRelativePosition(dataWrapper) // mouse position
    const points = [start.x, start.y, end.x, end.y]

    const connection: LifecycleConnection = {
        id: null,
        lifecycleId: lifecycle.id,
        phaseId: parentPhaseId,
        startPhaseId: startPhase.id,
        endPhaseId: null,
        startPhaseSide,
        endPhaseSide: null,
        points: JSON.stringify(points),
        pointsArray: points,
        doubleEnd: false,
        orthogonal: true,
    }
    dispatch(setLifecycleModelDrawConnection(connection))
    dispatch(setLifecyclePhaseActive(null))
}

export const EndDrawConnection = (
    e: Konva.KonvaEventObject<MouseEvent>,
    drawConnection: LifecycleConnection,
    endPhase: LifecyclePhase,
    endPhaseSide: Side,
    dispatch: Dispatch,
) => {
    e.cancelBubble = true
    if (e.evt.button !== 0) return

    const {
        startPhaseId,
        pointsArray,
    } = drawConnection
    const endPhaseId = endPhase.id

    const resultPoints = [
        pointsArray[0],
        pointsArray[1],
        pointsArray[pointsArray.length - 2],
        pointsArray[pointsArray.length - 1],
    ]

    const newConnection: LifecycleConnection = {
        ...drawConnection,
        id: startPhaseId < endPhaseId
            ? `${startPhaseId}_${endPhaseId}`
            : `${endPhaseId}_${startPhaseId}`,
        endPhaseId,
        endPhaseSide,
        points: JSON.stringify(resultPoints),
    }

    dispatch(setLifecycleModelLoadState(PENDING))
    AddLifecycleConnection(newConnection).then((res) => {
        dispatch(setLifecycleModelDrawConnection(null))

        if (res) {
            dispatch(setLifecyclePhaseActive(null))
            dispatch(setLifecycleConnectionActive(newConnection))
        }

        dispatch(setLifecycleModelReload(false))
        setTimeout(() => {
            dispatch(setLifecycleModelReload(true))
        }, 0)
    })
}

export const MouseEnterPhase = (
    drawConnection: LifecycleConnection,
    targetPhase: LifecyclePhase,
    dispatch: Dispatch,
) => {
    if (!drawConnection) return
    if (targetPhase.id === drawConnection.startPhaseId) return

    dispatch(setLifecyclePhaseActive(targetPhase))
}

export const StickTo = (
    e: Konva.KonvaEventObject<MouseEvent>,
    dataWrapper: Konva.Layer,
    drawConnection: LifecycleConnection,
    endPhaseSide: Side,
    dispatch: Dispatch,
) => {
    if (!drawConnection) return

    const end = GetRelativePosition(dataWrapper, e.currentTarget)
    const {
        pointsArray,
        orthogonal,
        startPhaseSide,
    } = drawConnection
    const newPoints = [pointsArray[0], pointsArray[1], end.x, end.y]

    if (orthogonal) {
        dispatch(setLifecycleModelDrawConnection({
            ...drawConnection,
            pointsArray: GetOrthogonalPoints(newPoints, startPhaseSide as Side, endPhaseSide as Side),
        }))
    } else {
        dispatch(setLifecycleModelDrawConnection({
            ...drawConnection,
            pointsArray: newPoints,
        }))
    }
}

export const FollowMouse = (
    dataWrapper: Konva.Layer,
    drawConnection: LifecycleConnection,
    dispatch: Dispatch,
) => {
    if (!drawConnection) return

    const {
        pointsArray,
        orthogonal,
        startPhaseSide,
    } = drawConnection
    const start = {
        x: pointsArray[0],
        y: pointsArray[1],
    }
    const end = GetRelativePosition(dataWrapper)
    const newPoints = [start.x, start.y, end.x, end.y]

    if (orthogonal) {
        let endPhaseSide

        if ([top, bottom].includes(startPhaseSide)) {
            endPhaseSide = end.y < start.y ? bottom : top
        }
        if ([left, right].includes(startPhaseSide)) {
            endPhaseSide = end.x < start.x ? right : left
        }

        dispatch(setLifecycleModelDrawConnection({
            ...drawConnection,
            pointsArray: GetOrthogonalPoints(newPoints, startPhaseSide as Side, endPhaseSide as Side),
        }))
    } else {
        dispatch(setLifecycleModelDrawConnection({
            ...drawConnection,
            pointsArray: newPoints,
        }))
    }
}
